import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Coins, Upload as UploadIcon, X, CheckCircle2, XCircle, Clock, Loader2, ShieldCheck, ExternalLink } from "lucide-react";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const STATUS_STYLES = {
  pending: "bg-sg-gold/15 text-sg-gold-deep border-sg-gold/40",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};
const STATUS_ICONS = { pending: Clock, approved: CheckCircle2, rejected: XCircle };

export default function AdminBillingUPI() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("pending");
  const [claims, setClaims] = useState([]);
  const [totals, setTotals] = useState({});
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // {claim, kind: 'approve'|'reject'}
  const [reviewerNotes, setReviewerNotes] = useState("");

  // Local form state so admin can edit before saving
  const [form, setForm] = useState({ vpa: "", payee_name: "", amount_inr: 999 });
  const [qrPreview, setQrPreview] = useState(null); // {name, dataUrl}

  const loadCfg = () => api.get("/admin/billing/upi/config").then(({ data }) => {
    setCfg(data);
    setForm({ vpa: data.vpa, payee_name: data.payee_name, amount_inr: data.amount_inr });
  });
  const loadClaims = () => {
    setClaimsLoading(true);
    api.get("/admin/billing/upi/claims", { params: { status: status === "any" ? undefined : status } })
      .then(({ data }) => { setClaims(data.claims || []); setTotals(data.totals || {}); })
      .finally(() => setClaimsLoading(false));
  };

  useEffect(() => { loadCfg(); }, []);
  useEffect(() => { loadClaims(); }, [status]);

  const onQrPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("QR image must be under 3 MB."); return; }
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file."); return; }
    setQrPreview({ name: f.name, dataUrl: await fileToBase64(f) });
  };

  const saveCfg = async () => {
    setSaving(true);
    try {
      const payload = { vpa: form.vpa.trim(), payee_name: form.payee_name.trim(), amount_inr: Number(form.amount_inr) };
      if (qrPreview) payload.qr_image_base64 = qrPreview.dataUrl;
      await api.put("/admin/billing/upi/config", payload);
      toast.success("UPI payment config updated. Agents see the new QR immediately.");
      setQrPreview(null);
      await loadCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const removeCustomQr = async () => {
    setSaving(true);
    try {
      await api.put("/admin/billing/upi/config", { clear_qr_image: true });
      toast.success("Removed custom QR — agents will now see a generated one.");
      await loadCfg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove QR.");
    } finally {
      setSaving(false);
    }
  };

  const submitReview = async () => {
    if (!reviewing) return;
    const { claim, kind } = reviewing;
    try {
      const { data } = await api.post(`/admin/billing/upi/claims/${claim.claim_id}/${kind}`, { reviewer_notes: reviewerNotes.trim() || null });
      const emailPart = data?.notification_sent
        ? " · agent notified via email"
        : (data?.notification_error === "resend_key_not_configured"
            ? " · email skipped (Resend key not set)"
            : data?.notification_error
              ? ` · email failed: ${data.notification_error}`
              : "");
      toast.success(
        (kind === "approve"
          ? `Approved — ${claim.agent_email} is now active`
          : `Rejected — ${claim.agent_email}'s claim was closed`)
        + emailPart,
      );
      setReviewing(null);
      setReviewerNotes("");
      loadClaims();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed.");
    }
  };

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">BILLING · UPI · SCAN &amp; PAY</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        UPI Payments &amp; <span className="font-serif-italic text-sg-gold-deep">Claim Review.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        Configure the VPA (UPI ID) or upload a bank-issued static QR. Then approve or reject the transfer claims submitted by agents.
      </p>

      {/* Config card */}
      <section className="mt-8 bg-white border border-sg-line rounded-3xl p-6 lg:p-8">
        {!cfg ? (
          <div className="text-sm text-sg-mute flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-8 items-start">
            <div className="mx-auto">
              <div className="bg-sg-cream-soft p-4 rounded-2xl border border-sg-line inline-block">
                {qrPreview ? (
                  <img src={qrPreview.dataUrl} alt="Custom QR preview" className="w-48 h-48 object-contain" data-testid="admin-upi-qr-preview" />
                ) : cfg.qr_image_url ? (
                  <img src={cfg.qr_image_url} alt="Uploaded QR" className="w-48 h-48 object-contain" data-testid="admin-upi-qr-current" />
                ) : (
                  <QRCodeSVG value={`upi://pay?pa=${encodeURIComponent(form.vpa || cfg.vpa)}&pn=${encodeURIComponent(form.payee_name || cfg.payee_name)}&am=${form.amount_inr || cfg.amount_inr}&cu=INR`} size={192} data-testid="admin-upi-qr-generated" />
                )}
              </div>
              <div className="mt-3 text-center">
                <div className="rupee text-2xl text-sg-navy">₹{form.amount_inr || cfg.amount_inr}</div>
                <div className="text-xs text-sg-mute font-mono truncate max-w-[220px]">{form.vpa || cfg.vpa}</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="overline text-sg-gold-deep">VPA / UPI ID</label>
                <Input
                  value={form.vpa}
                  onChange={(e) => setForm({ ...form, vpa: e.target.value })}
                  placeholder="yourbrand@icici"
                  className="mt-1 font-mono"
                  data-testid="admin-upi-vpa"
                />
                <p className="text-xs text-sg-mute mt-1">Format: <code>name@bank</code> (e.g., <code>inpropportal@icici</code>, <code>brand@okhdfcbank</code>)</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-sg-gold-deep">PAYEE NAME</label>
                  <Input
                    value={form.payee_name}
                    onChange={(e) => setForm({ ...form, payee_name: e.target.value })}
                    className="mt-1"
                    data-testid="admin-upi-payee"
                  />
                </div>
                <div>
                  <label className="overline text-sg-gold-deep">AMOUNT (INR)</label>
                  <Input
                    type="number"
                    min={1}
                    max={500000}
                    value={form.amount_inr}
                    onChange={(e) => setForm({ ...form, amount_inr: e.target.value })}
                    className="mt-1 font-mono"
                    data-testid="admin-upi-amount"
                  />
                </div>
              </div>

              <div>
                <label className="overline text-sg-gold-deep">CUSTOM QR IMAGE (OPTIONAL)</label>
                {qrPreview ? (
                  <div className="mt-1 flex items-center gap-3 bg-sg-cream-soft border border-sg-line rounded-xl p-2 pl-3">
                    <span className="text-sm truncate flex-1">Ready to save: {qrPreview.name}</span>
                    <button onClick={() => setQrPreview(null)} className="text-sg-mute hover:text-red-600" data-testid="admin-upi-qr-clear-preview">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : cfg.qr_image_url ? (
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <span className="text-sm text-sg-navy">Custom QR is currently in use.</span>
                    <label className="inline-flex items-center gap-2 border border-sg-line rounded-full px-3 py-1.5 text-sm cursor-pointer hover:border-sg-gold" data-testid="admin-upi-qr-replace">
                      <UploadIcon className="w-3.5 h-3.5" /> Replace
                      <input type="file" accept="image/*" className="hidden" onChange={onQrPick} />
                    </label>
                    <Button variant="outline" size="sm" onClick={removeCustomQr} disabled={saving} className="rounded-full text-red-700 border-red-200 hover:bg-red-50" data-testid="admin-upi-qr-remove">
                      Remove &amp; use generated
                    </Button>
                  </div>
                ) : (
                  <label className="mt-1 flex items-center gap-2 border border-dashed border-sg-line rounded-xl px-3 py-2 text-sm text-sg-mute cursor-pointer hover:border-sg-gold" data-testid="admin-upi-qr-upload">
                    <UploadIcon className="w-4 h-4" /> Upload a bank-issued QR (overrides the generated one). PNG / JPG, ≤ 3 MB.
                    <input type="file" accept="image/*" className="hidden" onChange={onQrPick} />
                  </label>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button
                  onClick={saveCfg}
                  disabled={saving || !form.vpa.trim()}
                  className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
                  data-testid="admin-upi-save"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save UPI config
                </Button>
                {cfg.updated_at && (
                  <span className="text-xs text-sg-mute font-mono">
                    Last updated · {new Date(cfg.updated_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Claims section */}
      <section className="mt-10">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="font-display text-3xl text-sg-navy">Claim queue</h2>
          <Badge className="rounded-full bg-sg-gold/15 text-sg-gold-deep border border-sg-gold/40" data-testid="admin-upi-total-pending">
            {totals.pending || 0} pending
          </Badge>
          <Badge className="rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="admin-upi-total-approved">
            {totals.approved || 0} approved
          </Badge>
          <Badge className="rounded-full bg-red-50 text-red-800 border border-red-200" data-testid="admin-upi-total-rejected">
            {totals.rejected || 0} rejected
          </Badge>
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className="text-sg-mute">Filter</span>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40 rounded-full border-sg-line" data-testid="admin-upi-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="any">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {claimsLoading ? (
          <div className="text-sm text-sg-mute py-8 flex items-center gap-2" data-testid="admin-upi-claims-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading claims…
          </div>
        ) : claims.length === 0 ? (
          <div className="text-sm text-sg-mute py-16 text-center border border-dashed border-sg-line rounded-3xl" data-testid="admin-upi-claims-empty">
            No {status === "any" ? "" : status + " "}claims yet.
          </div>
        ) : (
          <div className="space-y-3">
            {claims.map((c) => {
              const S = STATUS_ICONS[c.status] || Clock;
              return (
                <div key={c.claim_id} className="bg-white border border-sg-line rounded-2xl p-5 flex flex-col md:flex-row md:items-start gap-4" data-testid={`admin-upi-claim-${c.claim_id}`}>
                  <div className={`rounded-full p-2 border shrink-0 ${STATUS_STYLES[c.status] || STATUS_STYLES.pending}`}>
                    <S className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-sg-navy">{c.agent_name}</span>
                      <span className="text-sg-mute text-sm">·</span>
                      <span className="text-sm text-sg-mute font-mono">{c.agent_email}</span>
                      <Badge className={`rounded-full border ${STATUS_STYLES[c.status]}`}>{c.status.toUpperCase()}</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono text-sg-mute">
                      <span>UTR · <span className="text-sg-navy">{c.utr_or_reference}</span></span>
                      {c.paid_via && <span>via <span className="text-sg-navy">{c.paid_via}</span></span>}
                      <span>Amount · <span className="text-sg-navy">₹{c.amount_inr}</span></span>
                      <span>Paid to · <span className="text-sg-navy">{c.vpa_paid_to}</span></span>
                      <span>Submitted · <span className="text-sg-navy">{new Date(c.claimed_at).toLocaleString()}</span></span>
                    </div>
                    {c.notes && <div className="mt-2 text-xs text-sg-mute italic">&ldquo;{c.notes}&rdquo;</div>}
                    {c.screenshot_url && (
                      <a href={c.screenshot_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-sg-gold-deep hover:text-sg-navy underline decoration-dotted" data-testid={`admin-upi-claim-screenshot-${c.claim_id}`}>
                        View screenshot <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {c.reviewer_notes && (
                      <div className="mt-2 text-xs text-sg-navy border-l-2 border-sg-gold pl-3">
                        <span className="text-sg-mute">Reviewer note:</span> {c.reviewer_notes}
                      </div>
                    )}
                    {c.status !== "pending" && c.notification_at && (
                      <div className="mt-2 text-[11px] font-mono flex items-center gap-2" data-testid={`admin-upi-notif-${c.claim_id}`}>
                        {c.notification_sent ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Email sent · {new Date(c.notification_at).toLocaleString()}</span>
                        ) : c.notification_error === "resend_key_not_configured" ? (
                          <span className="inline-flex items-center gap-1 text-sg-mute"><Clock className="w-3 h-3" /> Email skipped (Resend key not set)</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700" title={c.notification_error || ""}><XCircle className="w-3 h-3" /> Email delivery failed</span>
                        )}
                      </div>
                    )}
                  </div>
                  {c.status === "pending" && (
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setReviewing({ claim: c, kind: "reject" }); setReviewerNotes(""); }}
                        className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
                        data-testid={`admin-upi-reject-${c.claim_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => { setReviewing({ claim: c, kind: "approve" }); setReviewerNotes(""); }}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`admin-upi-approve-${c.claim_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent className="max-w-md" data-testid="admin-upi-review-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-sg-navy">
              {reviewing?.kind === "approve" ? "Approve claim" : "Reject claim"}
            </DialogTitle>
            <DialogDescription>
              {reviewing?.kind === "approve"
                ? `You're activating ${reviewing?.claim?.agent_email}'s subscription for 30 days.`
                : `You're rejecting this claim. The agent will not be activated.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-2xl border border-sg-line bg-sg-cream-soft p-3 font-mono text-xs space-y-1">
              <div>UTR · {reviewing?.claim?.utr_or_reference}</div>
              <div>Amount · ₹{reviewing?.claim?.amount_inr}</div>
              <div>Paid to · {reviewing?.claim?.vpa_paid_to}</div>
              {reviewing?.claim?.paid_via && <div>Via · {reviewing?.claim?.paid_via}</div>}
            </div>
            <div>
              <label className="overline text-sg-gold-deep">REVIEWER NOTES (OPTIONAL)</label>
              <Textarea
                rows={3}
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                placeholder={reviewing?.kind === "approve" ? "e.g. Verified in ICICI statement 2026-07-04" : "e.g. UTR does not match any credit"}
                className="mt-1"
                data-testid="admin-upi-review-notes"
              />
            </div>
            <div className="text-xs text-sg-mute bg-sg-cream-soft border border-sg-line rounded-xl p-3 flex gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-sg-gold-deep shrink-0 mt-0.5" />
              <span>Once submitted this action cannot be reversed from the UI. Reversals must go through the ledger.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)} className="rounded-full border-sg-line" data-testid="admin-upi-review-cancel">Cancel</Button>
            <Button
              onClick={submitReview}
              className={`rounded-full text-white ${reviewing?.kind === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
              data-testid="admin-upi-review-confirm"
            >
              {reviewing?.kind === "approve" ? "Approve & activate" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
